import React, {useState} from 'react';
import {Alert, Box, Button, CircularProgress, Divider, Link, Stack, TextField, Typography} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Link as RouterLink, useNavigate, useSearchParams} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {useAuth} from '../providers/AuthProvider';
import {signIn, signInAsGuest} from '../services/authService';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';
import {KIOSK_VIEW} from '../utilities/kioskView';
import {SsoButtons} from '../components/account/SsoButtons';
import {SSO_ERROR_MESSAGES, ssoErrorFromSearch} from '../utilities/sso';

export default function LoginForm() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState<'login' | 'demo' | null>(null);
    const [errors, setErrors] = useState({email: '', password: ''});
    const {refreshAuth} = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const ssoError = ssoErrorFromSearch(`?${search}`);
    async function attempt(kind: 'login' | 'demo') {
        if (busy) return;
        setBusy(kind); setError('');
        try {
            const boardId = kind === 'demo' ? await signInAsGuest() : await signIn(email, password);
            queryClient.clear(); await refreshAuth();
            navigate(boardId ? `/boards/${boardId}?${KIOSK_VIEW}` : '/', {replace: true});
        } catch (failure) {
            setError(isNetworkError(failure) ? 'Unable to connect. Check your connection and try again.' : apiErrorMessage(failure));
        } finally { setBusy(null); }
    }
    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        const next = {email: /\S+@\S+\.\S+/.test(email) ? '' : 'Enter a valid email address', password: password ? '' : 'Enter your password'};
        setErrors(next);
        if (!next.email && !next.password) void attempt('login');
    };
    return <Box sx={{maxWidth: 440, mx: 'auto', pt: {xs: 2, sm: 2}, pb: 3}}>
        <Typography component="h1" variant="h3" sx={{mt: 1, mb: 1.5, fontSize: {xs: 34, sm: 38}}}>Your day, together.</Typography>
        <Typography color="text.secondary" sx={{mb: 3}}>A clear plan for your household, all in one place.</Typography>
        <Box component="form" onSubmit={submit} noValidate sx={{p: {xs: 2.5, sm: 3}, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3, boxShadow: '0 6px 24px rgba(37,59,54,0.035)'}}>
            <Stack spacing={2}>
                {error && <Alert severity="error">{error}</Alert>}
                {ssoError && !error && <Alert severity={ssoError === 'cancelled' ? 'info' : 'error'}>{SSO_ERROR_MESSAGES[ssoError]}</Alert>}
                <TextField label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} error={!!errors.email} helperText={errors.email} disabled={!!busy} required fullWidth/>
                <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={!!errors.password} helperText={errors.password} disabled={!!busy} required fullWidth/>
                <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{alignSelf: 'flex-end'}}>Forgot your password?</Link>
                <Button type="submit" variant="contained" disabled={!!busy} endIcon={busy === 'login' ? <CircularProgress size={17} color="inherit"/> : <ArrowForwardIcon/>} sx={{minHeight: 48}}>{busy === 'login' ? 'Signing in…' : 'Sign in'}</Button>
                <SsoButtons/>
                <Typography variant="body2" color="text.secondary" textAlign="center">New here? <Link component={RouterLink} to="/signup" sx={{fontWeight: 600}}>Create an account</Link></Typography>
                <Divider/>
                <Box><Button variant="outlined" fullWidth disabled={!!busy} onClick={() => void attempt('demo')} sx={{minHeight: 46}}>{busy === 'demo' ? 'Opening your demo…' : 'Try the demo'}</Button><Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{mt: 1}}>Explore a sample household. No sign-up needed.</Typography></Box>
            </Stack>
        </Box>
    </Box>;
}
